import { auth } from '../firebaseConfig';

export type Role = 'admin' | 'user';

export interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: Role;
}

function normalizeBaseUrl(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function getApiBaseUrl(): string {
  const explicitBase = (
    import.meta as unknown as { env: Record<string, unknown> }
  ).env.VITE_API_BASE_URL as string | undefined;
  if (explicitBase && explicitBase.length > 0) {
    return normalizeBaseUrl(explicitBase);
  }
  const projectId = (import.meta as unknown as { env: Record<string, unknown> })
    .env.VITE_FIREBASE_PROJECT_ID as string;
  const useEmulators =
    ((import.meta as unknown as { env: Record<string, unknown> }).env
      .VITE_USE_EMULATORS as string) === 'true';
  if (!projectId) throw new Error('VITE_FIREBASE_PROJECT_ID no está definido');
  if (useEmulators) {
    return `http://127.0.0.1:5001/${projectId}/us-central1/api`;
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/api`;
}

async function getAuthToken(): Promise<string | undefined> {
  const user = auth.currentUser;
  if (!user) return undefined;
  return user.getIdToken();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getApiBaseUrl();
  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(init?.headers || {}),
  };
  if (token)
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;

  const res = await fetch(`${base}${path}`, { ...init, headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as unknown as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export async function ensureUser(): Promise<AppUser> {
  return request<AppUser>('/auth/ensureUser', { method: 'POST' });
}

export async function getMe(): Promise<AppUser> {
  return request<AppUser>('/auth/me', { method: 'GET' });
}
